import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
  ArrowLeft, Download, Share2, Loader2, Edit3, Check, X, Trash2, Copy,
  ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles, Camera,
  Palette, Layers, Moon, Wand2, GripVertical, Film, Video as VideoIcon,
} from "lucide-react";
import { renderSlideBlob, renderCarouselFiles } from "../lib/clientCarouselRender";
import { SortableList, SortableItem } from "../components/SortableList";
import { horizontalListSortingStrategy } from "@dnd-kit/sortable";
import CrossCreatePrompt from "../components/CrossCreatePrompt";
import { LayoutPanel, LayoutDragHandle, resolveLayoutPreview } from "../components/CarouselLayoutPanel";
import { saveBlobToGallery, saveBase64ToGallery } from "../lib/shareUtil";

// Backend base URL — needed to build the public /reels/uploads/video/{id}
// URL used as src for <video> tags on video slides.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Hex (#rrggbb) → "r, g, b" string for rgba() interpolation.
const hexToRgb = (hex) => {
  try {
    const h = (hex || "").replace("#", "");
    const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    return `${parseInt(v.slice(0, 2), 16)}, ${parseInt(v.slice(2, 4), 16)}, ${parseInt(v.slice(4, 6), 16)}`;
  } catch {
    return "0, 0, 0";
  }
};

// HTML approximation of the text card that the canvas renderer bakes
// onto image slides. Used as a live overlay on top of <video> elements
// so the user sees the same headline/body composition. The exported
// MP4 is delivered RAW (no text baked in) — Instagram captions handle
// the messaging in production. This overlay is preview-only.
function VideoTextOverlay({ slide, carousel }) {
  const layout = resolveLayoutPreview(slide, carousel);
  const cardLeftPct = (layout.text_x - layout.card_width / 2) * 100;
  const cardTopPct = (layout.text_y - layout.card_height / 2) * 100;
  const cardWPct = layout.card_width * 100;
  const cardHPct = layout.card_height * 100;
  const bgRgb = hexToRgb(layout.card_bg_color || "#000000");
  const isFrosted = layout.card_shape === "frosted";
  const headline = (slide.headline || "").trim();
  const body = (slide.body || "").trim();
  if (!headline && !body) return null;
  return (
    <div
      className="absolute pointer-events-none flex flex-col justify-center overflow-hidden"
      style={{
        left: `${cardLeftPct}%`,
        top: `${cardTopPct}%`,
        width: `${cardWPct}%`,
        height: `${cardHPct}%`,
        borderRadius: layout.card_shape === "rect" ? 0 : 16,
        background: `rgba(${bgRgb}, ${layout.card_bg_alpha ?? 0.5})`,
        backdropFilter: isFrosted ? "blur(8px)" : undefined,
        WebkitBackdropFilter: isFrosted ? "blur(8px)" : undefined,
        padding: "5% 6%",
      }}
      data-testid="carousel-video-text-overlay"
    >
      {headline && (
        <div
          className="font-display font-black uppercase leading-[1.05] tracking-tight"
          style={{
            color: layout.text_color || "#ffffff",
            fontSize: "clamp(12px, 3.2cqw, 36px)",
            textShadow: "0 2px 8px rgba(0,0,0,0.6)",
          }}
        >
          {headline}
        </div>
      )}
      {body && (
        <div
          className="font-body leading-snug mt-2"
          style={{
            color: layout.text_color || "#ffffff",
            opacity: 0.92,
            fontSize: "clamp(9px, 2.0cqw, 22px)",
            textShadow: "0 2px 6px rgba(0,0,0,0.6)",
          }}
        >
          {body}
        </div>
      )}
    </div>
  );
}

// Legacy carousels (and some reels) had the LLM bake the full hashtag
// block into the caption text itself. Now that we cap hashtags at 5 and
// also append them separately, we MUST strip any trailing #-only block
// from the caption — otherwise users get 15 inline + 5 appended = 20
// hashtags pasted into Instagram.
const stripTrailingHashtags = (text) => {
  if (!text) return "";
  return text.replace(/(\s*#[\p{L}\p{N}_]+)+\s*$/u, "").trimEnd();
};

const CarouselDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [carousel, setCarousel] = useState(null);
  const [slidePreviews, setSlidePreviews] = useState([]); // [{ slide_number, dataUrl }]
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [editDraft, setEditDraft] = useState({ headline: "", body: "" });
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareReady, setShareReady] = useState(null); // {files, caption}
  const [busyImageSlide, setBusyImageSlide] = useState(0); // slide_number being processed, 0 = none
  const [editPromptOpen, setEditPromptOpen] = useState(false);
  const [editPromptDraft, setEditPromptDraft] = useState("");
  const [bodyStyleBusy, setBodyStyleBusy] = useState(false);
  const [aiBgPromptOpen, setAiBgPromptOpen] = useState(false);
  const [aiBgPromptDraft, setAiBgPromptDraft] = useState("");
  // Cross-format prompt — opens automatically when arriving here just after
  // generation (sessionStorage flag set by ReelCreator), and on demand via
  // the banner button.
  const [crossOpen, setCrossOpen] = useState(false);
  // When this carousel is already paired with a reel (campaign_id present),
  // we look up the paired reel so the banner can show "Apri reel" instead
  // of re-prompting the cross-create modal.
  const [pairedReelId, setPairedReelId] = useState(null);
  // Layout customization (carousel-wide defaults). When layout_mode is
  // "custom", every slide is rendered with the natural bg image + a colored
  // text card the user can position/style. The previewer reads carousel.layout
  // straight off state, so any tweak triggers a live re-render.
  const [savingLayout, setSavingLayout] = useState(false);

  useEffect(() => {
    api.get(`/carousels/${id}`)
      .then((r) => setCarousel(r.data))
      .catch(() => toast.error("Carosello non trovato"))
      .finally(() => setLoading(false));
    // We intentionally DON'T auto-open the cross-create modal anymore: if the
    // user just came from the reel→carousel flow, the new carousel is already
    // linked (campaign_id is set) and re-prompting would suggest a content
    // type they explicitly just created. Drop the legacy flag if present.
    try { sessionStorage.removeItem("cross_create:just_arrived"); } catch (_) {}
  }, [id]);

  // Resolve the paired reel (if any) so the banner can offer "Apri reel"
  // instead of "Crea reel" when this carousel is already in a campaign.
  useEffect(() => {
    const cid = carousel?.campaign_id;
    if (!cid) { setPairedReelId(null); return; }
    let cancelled = false;
    api.get(`/campaigns/${cid}`)
      .then((r) => { if (!cancelled) setPairedReelId(r.data?.reel_id || null); })
      .catch(() => { if (!cancelled) setPairedReelId(null); });
    return () => { cancelled = true; };
  }, [carousel?.campaign_id]);

  // Reset image-edit prompt when the user navigates between slides — keeping
  // the previous prompt would confuse the UX.
  useEffect(() => {
    setEditPromptOpen(false);
    setEditPromptDraft("");
  }, [activeSlide]);

  // Whenever carousel changes, regenerate the slide PNG previews on a canvas.
  // Keeping previews in state lets the user see live updates after edits
  // without needing to call the server. Video slides are skipped here —
  // their preview is the <video> element shown directly above.
  useEffect(() => {
    if (!carousel) return;
    let cancelled = false;
    (async () => {
      const out = [];
      for (const s of carousel.slides) {
        if (s.video_id) {
          // No PNG preview for video slides — the active-slide pane
          // renders a <video> instead and the thumbnail rail shows a
          // film-icon placeholder.
          continue;
        }
        const blob = await renderSlideBlob(s, carousel);
        const url = URL.createObjectURL(blob);
        out.push({ slide_number: s.slide_number, dataUrl: url });
      }
      if (!cancelled) {
        setSlidePreviews((prev) => {
          for (const p of prev) URL.revokeObjectURL(p.dataUrl);
          return out;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [carousel]);

  const onDownload = async () => {
    if (!carousel) return;
    setDownloading(true);
    try {
      const safe = (carousel.business_name || "carousel").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
      const slides = carousel.slides || [];
      let saved = 0;
      for (let i = 0; i < slides.length; i++) {
        if (slides[i].video_id) {
          // Video slide: fetch the original .mp4 from the backend and
          // hand it to the gallery saver. Same UX as a PNG slide.
          try {
            const res = await fetch(`${BACKEND_URL}/api/reels/uploads/video/${slides[i].video_id}`);
            if (!res.ok) throw new Error("fetch failed");
            const vBlob = await res.blob();
            const ok = await saveBlobToGallery(
              vBlob,
              `${safe}-slide_${String(i + 1).padStart(2, "0")}.mp4`,
              { title: `Slide ${i + 1} (video)` },
            );
            if (ok) saved += 1;
          } catch (vErr) {
            // Continue with the other slides even if one video fails.
          }
        } else {
          const blob = await renderSlideBlob(slides[i], carousel);
          const ok = await saveBlobToGallery(
            blob,
            `${safe}-slide_${String(i + 1).padStart(2, "0")}.png`,
            { title: `Slide ${i + 1}` /* useShare:false → silent download */ },
          );
          if (ok) saved += 1;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      if (saved > 0) toast.success(`${saved} file scaricati!`);
    } catch (e) {
      toast.error(e?.message || "Errore download");
    } finally {
      setDownloading(false);
    }
  };

  // Download all CLEAN background images (no text overlays) — straight from
  // the bare AI image. Skips slides without a usable bg image.
  const onDownloadCleanPhotos = async () => {
    if (!carousel) return;
    setDownloading(true);
    try {
      const safe = (carousel.business_name || "carousel").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
      const slides = carousel.slides || [];
      let saved = 0;
      for (let i = 0; i < slides.length; i++) {
        const s = slides[i];
        const b64 = s.image_base64 || carousel.body_bg_image || "";
        if (!b64) continue;
        const ok = await saveBase64ToGallery(
          b64,
          `${safe}-slide_${String(i + 1).padStart(2, "0")}-foto.jpg`,
          "image/jpeg",
          { title: `Foto slide ${i + 1}` /* useShare:false */ },
        );
        if (ok) saved += 1;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (saved > 0) toast.success(`${saved} foto scaricate (senza scritte)`);
      else toast.info("Nessuna foto disponibile per questo carosello");
    } catch (e) {
      toast.error(e?.message || "Errore download");
    } finally {
      setDownloading(false);
    }
  };

  // Web Share API requires the actual share() call to happen inside the
  // user-gesture click handler, NOT after a long async render. So we render
  // the files first, stash them in state, and then expose a "Tocca per
  // condividere" button that fires share() synchronously on click.
  const onPrepareShare = async () => {
    if (!carousel) return;
    if (!navigator.share || !navigator.canShare) {
      // Fallback: download the ZIP so the user uploads manually.
      return onDownload();
    }
    // Mixed image+video carousels: many target apps (TikTok, WhatsApp,
    // Telegram on some Android builds) reject the share when the file
    // array mixes image/* and video/*. Instagram handles it but only via
    // its native sheet — testing showed crashes on TikTok specifically.
    // Default to the download path for mixed media so the user can post
    // from their gallery (Instagram auto-detects mixed albums; TikTok
    // accepts the video alone). Pure-image carousels keep the share UX.
    const hasVideo = (carousel.slides || []).some((s) => s.video_id);
    if (hasVideo) {
      toast.info("Carosello misto foto + video → scarico tutto in galleria così puoi pubblicarlo manualmente su Instagram", { duration: 4500 });
      return onDownload();
    }
    setSharing(true);
    try {
      const files = await renderCarouselFiles(carousel, { backendUrl: BACKEND_URL });
      const canShare = navigator.canShare({ files });
      if (!canShare) {
        toast.info("Il tuo browser non supporta la condivisione di più file. Scarico in galleria.");
        await onDownload();
        return;
      }
      setShareReady({ files });
    } catch (e) {
      toast.error(e?.message || "Errore preparazione condivisione");
    } finally {
      setSharing(false);
    }
  };

  const onShareNow = () => {
    if (!shareReady?.files) return;
    let p;
    try {
      p = navigator.share({
        files: shareReady.files,
        title: carousel?.title || "Il mio carosello",
        text: stripTrailingHashtags(carousel?.caption || ""),
      });
    } catch (e) {
      toast.error(e?.message || "Condivisione fallita");
      return;
    }
    Promise.resolve(p)
      .then(() => {
        toast.success("Condiviso!");
        setShareReady(null);
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          toast.error(err?.message || "Condivisione fallita");
        }
      });
  };

  const onCopyCaption = () => {
    if (!carousel) return;
    const cleanCaption = stripTrailingHashtags(carousel.caption || "");
    const tags = (carousel.hashtags || []).slice(0, 5).map((h) => `#${h}`).join(" ");
    const text = cleanCaption + (tags ? `\n\n${tags}` : "");
    navigator.clipboard.writeText(text);
    toast.success("Caption + hashtag copiati!");
  };

  const onDelete = async () => {
    if (!window.confirm("Eliminare questo carosello?")) return;
    try {
      await api.delete(`/carousels/${id}`);
      toast.success("Carosello eliminato");
      navigate("/dashboard");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const startEdit = (idx) => {
    const s = carousel.slides[idx];
    setEditDraft({ headline: s.headline, body: s.body });
    setEditingIdx(idx);
  };

  const saveEdit = async () => {
    if (editingIdx < 0 || !carousel) return;
    const s = carousel.slides[editingIdx];
    try {
      await api.patch(`/carousels/${id}/slides/${s.slide_number}`, {
        headline: editDraft.headline,
        body: editDraft.body,
      });
      const next = { ...carousel };
      next.slides = carousel.slides.map((sl, i) =>
        i === editingIdx ? { ...sl, headline: editDraft.headline, body: editDraft.body } : sl
      );
      setCarousel(next);
      setEditingIdx(-1);
      toast.success("Slide aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const onRegenerateImage = async (slideNumber, options = {}) => {
    // options: { prompt: string|null, editMode: boolean|null }
    const { prompt = null, editMode = null } = options;
    if (!options.prompt && !window.confirm("Rigenerare l'immagine? Consumerà 1 credito immagine.")) return;
    try {
      setBusyImageSlide(slideNumber);
      const { data } = await api.post(
        `/carousels/${id}/slides/${slideNumber}/regenerate-image`,
        { prompt, edit_mode: editMode },
      );
      const next = { ...carousel };
      next.slides = carousel.slides.map((sl) =>
        sl.slide_number === slideNumber ? { ...sl, image_base64: data.slide.image_base64, image_prompt: data.slide.image_prompt } : sl
      );
      setCarousel(next);
      setEditPromptOpen(false);
      setEditPromptDraft("");
      toast.success("Immagine aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rigenerazione");
    } finally {
      setBusyImageSlide(0);
    }
  };

  const onAiEditWithPrompt = (slideNumber) => {
    if (!editPromptDraft.trim()) {
      toast.error("Scrivi cosa vuoi modificare");
      return;
    }
    onRegenerateImage(slideNumber, { prompt: editPromptDraft.trim(), editMode: true });
  };

  // Change the body-slides background style for the whole carousel. The
  // server stores the choice + (optionally) an AI-generated photo; the
  // canvas renderer reads `body_style` and `body_bg_image` to draw each
  // body slide accordingly. `ai_photo` costs 1 image credit.
  const onChangeBodyStyle = async (style, opts = {}) => {
    if (!carousel) return;
    if (bodyStyleBusy) return;
    if (style === "ai_photo" && !opts.prompt) {
      // Open the prompt panel — the user must confirm a prompt before we
      // burn a credit, so they can steer the AI photo.
      setAiBgPromptOpen(true);
      return;
    }
    try {
      setBodyStyleBusy(true);
      const payload = { body_style: style };
      if (style === "ai_photo" && opts.prompt) {
        payload.ai_photo_prompt = opts.prompt;
      }
      const { data } = await api.patch(`/carousels/${id}/body-style`, payload);
      setCarousel({
        ...carousel,
        body_style: data.body_style,
        body_bg_image: data.body_bg_image || "",
      });
      setAiBgPromptOpen(false);
      setAiBgPromptDraft("");
      toast.success("Sfondo slide aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento sfondo");
    } finally {
      setBodyStyleBusy(false);
    }
  };

  // ============== LAYOUT (custom mode) ==============
  // The carousel-level defaults live on `carousel.layout`; per-slide
  // overrides on `slide.layout`. We update state locally first (so the
  // preview canvas re-renders instantly), then push the change to Mongo.
  // PATCH is fire-and-forget: if the request fails the UI rolls back.
  const onToggleLayoutMode = async () => {
    if (!carousel || savingLayout) return;
    const next = (carousel.layout_mode || "auto") === "custom" ? "auto" : "custom";
    const prev = carousel;
    setCarousel({ ...carousel, layout_mode: next });
    try {
      setSavingLayout(true);
      await api.patch(`/carousels/${id}/layout`, { layout_mode: next });
    } catch (e) {
      setCarousel(prev);
      toast.error("Errore salvataggio");
    } finally {
      setSavingLayout(false);
    }
  };

  // Patch the carousel-wide layout defaults. `partial` only contains the
  // fields the user changed; missing keys fall back through the chain.
  const onUpdateLayout = async (partial) => {
    if (!carousel) return;
    const nextLayout = { ...(carousel.layout || {}), ...partial };
    const prev = carousel;
    setCarousel({ ...carousel, layout: nextLayout });
    try {
      await api.patch(`/carousels/${id}/layout`, { layout: nextLayout });
    } catch {
      setCarousel(prev);
      toast.error("Errore salvataggio layout");
    }
  };

  // Patch the override for a single slide. Pass null/empty to reset.
  const onUpdateSlideLayout = async (slideNumber, partial) => {
    if (!carousel) return;
    const idx = (carousel.slides || []).findIndex((s) => s.slide_number === slideNumber);
    if (idx < 0) return;
    const cur = carousel.slides[idx].layout || null;
    const nextLayout = partial === null ? null : { ...(cur || {}), ...partial };
    const newSlides = carousel.slides.map((s, i) => i === idx ? { ...s, layout: nextLayout } : s);
    const prev = carousel;
    setCarousel({ ...carousel, slides: newSlides });
    try {
      await api.patch(`/carousels/${id}/slides/${slideNumber}/layout`, { layout: nextLayout });
    } catch {
      setCarousel(prev);
      toast.error("Errore salvataggio");
    }
  };

  // User uploads their own background image. Read file → base64 → send to
  // backend. We use FileReader instead of fetch+blob because base64 is what
  // MongoDB stores and what canvas renderer expects.
  const onPickImage = (slideNumber) => (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking same file
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.split(",")[1] || dataUrl;
      try {
        setBusyImageSlide(slideNumber);
        const { data } = await api.post(
          `/carousels/${id}/slides/${slideNumber}/set-image`,
          { image_base64: base64 },
        );
        const next = { ...carousel };
        next.slides = carousel.slides.map((sl) =>
          sl.slide_number === slideNumber ? { ...sl, image_base64: data.slide.image_base64 } : sl
        );
        setCarousel(next);
        toast.success("Immagine caricata");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore upload");
      } finally {
        setBusyImageSlide(0);
      }
    };
    reader.onerror = () => toast.error("Errore lettura file");
    reader.readAsDataURL(file);
  };

  // Pick a video from gallery or camera, upload it to the shared
  // /reels/uploads/video endpoint, then attach the resulting video_id
  // to this slide. The slide becomes a VIDEO slide (mixed-media
  // carousels are supported natively by Instagram).
  const onPickVideo = (slideNumber) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 120 * 1024 * 1024) {
      toast.error("Video troppo grande (max 120MB)");
      return;
    }
    try {
      setBusyImageSlide(slideNumber);
      const fd = new FormData();
      fd.append("file", file);
      const upRes = await api.post(`/reels/uploads/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      const videoId = upRes.data?.video_id;
      if (!videoId) throw new Error("Upload fallito");
      const { data } = await api.patch(
        `/carousels/${id}/slides/${slideNumber}/video`,
        { video_id: videoId },
      );
      const next = { ...carousel };
      next.slides = carousel.slides.map((sl) =>
        sl.slide_number === slideNumber
          ? { ...sl, video_id: data.slide?.video_id || videoId, image_id: null, image_base64: "" }
          : sl
      );
      setCarousel(next);
      toast.success("Video caricato e collegato alla slide");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Errore upload video");
    } finally {
      setBusyImageSlide(0);
    }
  };

  // Detach a video from a slide (slide reverts to image / brand gradient).
  const onRemoveSlideVideo = async (slideNumber) => {
    try {
      setBusyImageSlide(slideNumber);
      await api.patch(`/carousels/${id}/slides/${slideNumber}/video`, { video_id: "" });
      const next = { ...carousel };
      next.slides = carousel.slides.map((sl) =>
        sl.slide_number === slideNumber ? { ...sl, video_id: null } : sl
      );
      setCarousel(next);
      toast.success("Video rimosso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimozione");
    } finally {
      setBusyImageSlide(0);
    }
  };

  // Drag & drop reorder. The `newIds` are the (current) slide_numbers in the
  // new desired order. We optimistically update the local state so the user
  // sees an instant rearrange, then call the backend (which renumbers 1..N
  // and returns the new carousel). We refresh state from the server response
  // to stay in sync.
  const onReorderSlides = async (newIds, { oldIndex, newIndex }) => {
    if (!carousel) return;
    if (newIndex === oldIndex) return;
    const previous = carousel;
    // Optimistic update — rebuild slides in new order
    const bySlideNumber = new Map(carousel.slides.map((s) => [s.slide_number, s]));
    const reorderedSlides = newIds.map((sn, i) => ({
      ...bySlideNumber.get(sn),
      slide_number: i + 1, // mirror server renumbering immediately
    }));
    setCarousel({ ...carousel, slides: reorderedSlides });
    // Keep the user's "active slide" pointer on the same content item
    // they were viewing (its index may have shifted).
    if (oldIndex === activeSlide) {
      setActiveSlide(newIndex);
    } else if (oldIndex < activeSlide && newIndex >= activeSlide) {
      setActiveSlide(activeSlide - 1);
    } else if (oldIndex > activeSlide && newIndex <= activeSlide) {
      setActiveSlide(activeSlide + 1);
    }
    try {
      const { data } = await api.put(`/carousels/${id}/slides/reorder`, { order: newIds });
      setCarousel(data);
      toast.success("Ordine aggiornato");
    } catch (e) {
      setCarousel(previous);
      toast.error(e?.response?.data?.detail || "Errore riordino");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 flex items-center gap-3 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin text-[#FF4400]" />
          <span className="font-mono text-xs uppercase tracking-widest">Caricamento carosello...</span>
        </div>
      </div>
    );
  }

  if (!carousel) return null;

  const activeSrc = slidePreviews.find((p) => p.slide_number === carousel.slides[activeSlide]?.slide_number)?.dataUrl;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white mb-6 font-mono text-[11px] uppercase tracking-widest"
          data-testid="carousel-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Dashboard
        </button>

        <div className="mb-3">
          <span className="inline-block px-3 py-1 bg-[#FF4400]/15 text-[#FF4400] text-[10px] font-mono uppercase tracking-widest rounded-sm">
            · Carosello · {carousel.template}
          </span>
        </div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mb-2">
          {carousel.title}
        </h1>
        <p className="text-zinc-400 font-body mb-8">
          <span className="text-[#FF4400]">{carousel.business_name}</span> · {carousel.slides.length} slide
        </p>

        {/* Cross-create banner: propose a matching reel from this carousel.
            When the carousel is already linked to a reel (campaign_id +
            paired reel resolved) we flip the banner to "open the existing
            reel" instead of suggesting another generation. */}
        <div className="mb-8 rounded-lg border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-[#FF4400]/5 to-transparent p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-md bg-gradient-to-br from-violet-500 to-[#FF4400] flex items-center justify-center shadow-sm">
              <Film className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-300">· Idea ↔ Reel</div>
              <p className="text-sm text-white font-body truncate">
                {pairedReelId ? (
                  <>Hai già un <strong>reel video</strong> collegato a questa idea.</>
                ) : (
                  <>Vuoi creare anche un <strong>reel video</strong> con la stessa idea?</>
                )}
              </p>
            </div>
          </div>
          {pairedReelId ? (
            <button
              onClick={() => navigate(`/reel/${pairedReelId}`)}
              data-testid="carousel-open-paired-reel"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white font-display font-bold text-sm rounded-md transition-colors shrink-0"
            >
              <Film className="w-4 h-4" />
              Apri reel
            </button>
          ) : (
            <button
              onClick={() => setCrossOpen(true)}
              data-testid="carousel-cross-create-button"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white font-display font-bold text-sm rounded-md transition-colors shrink-0"
            >
              <Sparkles className="w-4 h-4" />
              Crea reel
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* Left: large slide preview + thumbnail rail */}
          <div>
            <div
              className="relative w-full bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden"
              style={{ aspectRatio: "1080/1350", maxWidth: 540, margin: "0 auto", containerType: "inline-size" }}
              data-testid="carousel-active-preview"
            >
              {carousel.slides[activeSlide]?.video_id ? (
                <>
                  <video
                    src={`${BACKEND_URL}/api/reels/uploads/video/${carousel.slides[activeSlide].video_id}`}
                    controls
                    playsInline
                    className="w-full h-full object-cover bg-black"
                    data-testid="carousel-active-video"
                  />
                  {/* HTML text card overlay — matches the canvas-rendered
                      card on image slides so the user gets a consistent
                      preview. Pointer-none so the video player remains
                      interactive underneath. */}
                  <VideoTextOverlay
                    slide={carousel.slides[activeSlide]}
                    carousel={carousel}
                  />
                </>
              ) : activeSrc ? (
                <img
                  src={activeSrc}
                  alt={`Slide ${activeSlide + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-600">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}

              {/* Draggable card overlay — only visible when layout_mode is
                  "custom". Lets the user position the text card by simply
                  grabbing it. Coordinates are normalized to (0..1) and saved
                  on drop. */}
              {carousel.layout_mode === "custom" && carousel.slides[activeSlide] && (
                <LayoutDragHandle
                  slide={carousel.slides[activeSlide]}
                  carousel={carousel}
                  hasBody={!!(carousel.slides[activeSlide]?.body || "").trim()}
                  onChange={(partial) => onUpdateSlideLayout(carousel.slides[activeSlide].slide_number, partial)}
                />
              )}

              {carousel.slides.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveSlide((i) => Math.max(0, i - 1))}
                    disabled={activeSlide === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center disabled:opacity-30"
                    data-testid="carousel-prev-slide"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setActiveSlide((i) => Math.min(carousel.slides.length - 1, i + 1))}
                    disabled={activeSlide === carousel.slides.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center disabled:opacity-30"
                    data-testid="carousel-next-slide"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 bg-black/60 backdrop-blur px-2 py-1 rounded-full">
                {carousel.slides.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      i === activeSlide ? "bg-[#FF4400] w-4" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Thumbnail rail — draggable to reorder slides.
                Larger thumbnails (w-24/w-28) than the original w-16 so the
                drag handle is easy to hit on mobile and the user can read
                the slide content without zooming. */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  · Trascina per riordinare
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                  {carousel.slides.length} slide
                </div>
              </div>
              <SortableList
                items={carousel.slides.map((s) => s.slide_number)}
                strategy={horizontalListSortingStrategy}
                onReorder={onReorderSlides}
              >
                <div className="flex gap-3 overflow-x-auto pb-3">
                  {carousel.slides.map((s, i) => {
                    const url = slidePreviews.find((p) => p.slide_number === s.slide_number)?.dataUrl;
                    const sel = i === activeSlide;
                    return (
                      <SortableItem
                        key={s.slide_number}
                        id={s.slide_number}
                        testId={`carousel-thumb-${s.slide_number}`}
                        className={`relative flex-shrink-0 w-24 sm:w-28 border-2 rounded-sm overflow-hidden transition-all cursor-grab active:cursor-grabbing ${
                          sel ? "border-[#FF4400] -translate-y-1 shadow-lg shadow-[#FF4400]/30" : "border-zinc-800 hover:border-zinc-500"
                        }`}
                      >
                        <div
                          onClick={() => setActiveSlide(i)}
                          style={{ aspectRatio: "1080/1350" }}
                          className="w-full"
                        >
                          {s.video_id ? (
                            <div className="relative w-full h-full bg-zinc-900 flex items-center justify-center">
                              <Film className="w-5 h-5 text-zinc-500" />
                              <span className="absolute bottom-1 right-1 text-[9px] font-mono uppercase tracking-widest text-zinc-400 bg-black/60 px-1 rounded-sm">
                                Video
                              </span>
                            </div>
                          ) : url ? (
                            <img src={url} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                          ) : (
                            <div className="w-full h-full bg-zinc-900" />
                          )}
                        </div>
                        {/* Slide number badge — bigger so it reads at a glance */}
                        <div className="absolute top-1.5 left-1.5 bg-black/75 backdrop-blur rounded-sm px-1.5 py-0.5 text-[11px] font-mono font-bold text-white pointer-events-none">
                          {i + 1}
                        </div>
                        {/* Drag handle — clearly visible so the user knows
                            the thumb is grabbable. */}
                        <div className="absolute top-1.5 right-1.5 bg-black/75 backdrop-blur rounded-sm p-1 text-white pointer-events-none">
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>
                        {/* Slide role label at the bottom */}
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 pt-3 pb-1 pointer-events-none">
                          <div className="font-mono text-[9px] uppercase tracking-widest text-white/90 truncate">
                            {s.role}
                          </div>
                        </div>
                      </SortableItem>
                    );
                  })}
                </div>
              </SortableList>
            </div>
          </div>

          {/* Right column: text editor + actions */}
          <div className="space-y-4">
            {/* Macro selector: 2 simple background modes.
                A) "custom" → use slide photos + draggable text card
                B) "auto"   → use proposed backgrounds + full-page text */}
            <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5" data-testid="carousel-bg-macro-panel">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] mb-3 flex items-center gap-2">
                <Palette className="w-3 h-3" />
                · Sfondo del carosello
              </div>
              <div className="grid grid-cols-1 gap-2">
                <button
                  onClick={() => { if ((carousel.layout_mode || "auto") !== "custom") onToggleLayoutMode(); }}
                  disabled={savingLayout}
                  data-testid="carousel-bg-macro-custom"
                  className={`text-left border rounded-sm p-3 transition-colors disabled:opacity-40 ${
                    (carousel.layout_mode || "auto") === "custom"
                      ? "border-[#FF4400] bg-[#FF4400]/10 ring-1 ring-[#FF4400]"
                      : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 shrink-0 rounded-sm flex items-center justify-center ${
                      (carousel.layout_mode || "auto") === "custom" ? "bg-[#FF4400] text-white" : "bg-zinc-800 text-zinc-400"
                    }`}>
                      <Camera className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black text-sm text-white tracking-tight">
                        Foto del reel + drag & drop
                      </div>
                      <div className="font-body text-[11px] text-zinc-400 leading-snug mt-1">
                        Usa le foto del carosello come sfondo. Trascina liberamente le scritte sopra ogni slide.
                      </div>
                    </div>
                    {(carousel.layout_mode || "auto") === "custom" && (
                      <div className="w-4 h-4 bg-[#FF4400] rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => { if ((carousel.layout_mode || "auto") === "custom") onToggleLayoutMode(); }}
                  disabled={savingLayout}
                  data-testid="carousel-bg-macro-auto"
                  className={`text-left border rounded-sm p-3 transition-colors disabled:opacity-40 ${
                    (carousel.layout_mode || "auto") !== "custom"
                      ? "border-[#FF4400] bg-[#FF4400]/10 ring-1 ring-[#FF4400]"
                      : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 shrink-0 rounded-sm flex items-center justify-center ${
                      (carousel.layout_mode || "auto") !== "custom" ? "bg-[#FF4400] text-white" : "bg-zinc-800 text-zinc-400"
                    }`}>
                      <Layers className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black text-sm text-white tracking-tight">
                        Sfondi proposti + testo a tutta pagina
                      </div>
                      <div className="font-body text-[11px] text-zinc-400 leading-snug mt-1">
                        Scegli uno sfondo proposto (gradiente, scuro, sfocato o foto AI). Il testo viene composto automaticamente.
                      </div>
                    </div>
                    {(carousel.layout_mode || "auto") !== "custom" && (
                      <div className="w-4 h-4 bg-[#FF4400] rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Mode A: custom layout controls (shape, colors, drag handle on preview) */}
            {(carousel.layout_mode || "auto") === "custom" && (
              <LayoutPanel
                carousel={carousel}
                activeSlide={carousel.slides[activeSlide]}
                onToggleMode={onToggleLayoutMode}
                onUpdateLayout={onUpdateLayout}
                onUpdateSlideLayout={onUpdateSlideLayout}
                savingLayout={savingLayout}
                hideToggle={true}
              />
            )}

            {/* Mode B: proposed backgrounds picker.
                Body-style is a carousel-wide setting → show this panel for
                every slide role (not just body) so the user can pick the
                proposed background from any slide. */}
            {(carousel.layout_mode || "auto") !== "custom" && (
              <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5" data-testid="carousel-body-style-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                    · Scegli lo sfondo proposto
                  </div>
                  {bodyStyleBusy && <Loader2 className="w-3 h-3 animate-spin text-[#FF4400]" />}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: "gradient", label: "Gradiente", sub: "Brand", icon: Palette,
                      preview: `linear-gradient(135deg, ${carousel.brand_color_primary || "#FF4400"} 0%, ${carousel.brand_color_secondary || "#FFB800"} 100%)` },
                    { id: "solid_dark", label: "Scuro", sub: "Solido", icon: Moon,
                      preview: "radial-gradient(circle at 85% 15%, rgba(255,68,0,0.35) 0%, #0a0a0c 70%)" },
                    { id: "blur_cover", label: "Sfocato", sub: "Cover", icon: Layers,
                      preview: null /* shows the cover image */ },
                    { id: "ai_photo", label: "Foto AI", sub: "·1 credito", icon: Wand2,
                      preview: null /* shows the saved AI photo if present */ },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = (carousel.body_style || "gradient") === opt.id;
                    let bgImage = null;
                    if (opt.id === "blur_cover") {
                      const cov = (carousel.slides || []).find((s) => s.role === "cover");
                      if (cov?.image_base64) {
                        bgImage = `url(data:image/jpeg;base64,${cov.image_base64})`;
                      }
                    } else if (opt.id === "ai_photo" && carousel.body_bg_image) {
                      bgImage = `url(data:image/jpeg;base64,${carousel.body_bg_image})`;
                    }
                    return (
                      <button
                        key={opt.id}
                        onClick={() => onChangeBodyStyle(opt.id)}
                        disabled={bodyStyleBusy}
                        data-testid={`carousel-body-style-${opt.id}`}
                        className={`relative overflow-hidden border rounded-sm p-3 text-left transition-all disabled:opacity-40 ${
                          active
                            ? "border-[#FF4400] ring-1 ring-[#FF4400]"
                            : "border-zinc-800 hover:border-zinc-600"
                        }`}
                        style={{
                          background: bgImage || opt.preview || "#18181b",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          filter: opt.id === "blur_cover" && bgImage ? "blur(0px)" : undefined,
                        }}
                      >
                        {opt.id === "blur_cover" && bgImage && (
                          <div className="absolute inset-0" style={{
                            background: `${bgImage}`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "blur(10px) saturate(120%)",
                            transform: "scale(1.1)",
                          }} />
                        )}
                        <div className="absolute inset-0 bg-black/45" />
                        <div className="relative flex flex-col gap-1">
                          <Icon className="w-4 h-4 text-white" />
                          <div className="font-display font-black text-xs text-white tracking-tight">
                            {opt.label}
                          </div>
                          <div className="font-mono text-[9px] uppercase tracking-widest text-white/70">
                            {opt.sub}
                          </div>
                        </div>
                        {active && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#FF4400] rounded-full flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {aiBgPromptOpen && (
                  <div className="mt-3 space-y-2" data-testid="carousel-ai-bg-prompt-panel">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      Descrivi lo sfondo che vuoi (opzionale)
                    </div>
                    <textarea
                      rows={2}
                      value={aiBgPromptDraft}
                      onChange={(e) => setAiBgPromptDraft(e.target.value)}
                      placeholder="Es. ambiente minimal con luci soft, palette pastello, texture marmo chiaro..."
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-sm rounded-sm font-body focus:outline-none focus:ring-1 focus:ring-[#FF4400] resize-none"
                      data-testid="carousel-ai-bg-prompt-input"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setAiBgPromptOpen(false); setAiBgPromptDraft(""); }}
                        className="px-3 py-2 text-xs text-zinc-400 hover:text-white font-mono uppercase tracking-widest"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={() => onChangeBodyStyle("ai_photo", { prompt: aiBgPromptDraft.trim() || "auto" })}
                        disabled={bodyStyleBusy}
                        data-testid="carousel-ai-bg-prompt-submit"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold text-xs rounded-sm transition-colors"
                      >
                        {bodyStyleBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Genera foto sfondo
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600 leading-relaxed">
                  · Applica a tutte le slide testuali del carosello
                </div>
              </div>
            )}

            {/* Active slide editor */}
            <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Slide {activeSlide + 1}/{carousel.slides.length} · <span className="text-[#FF4400]">{carousel.slides[activeSlide]?.role}</span>
                </div>
                {editingIdx !== activeSlide ? (
                  <button
                    onClick={() => startEdit(activeSlide)}
                    data-testid="carousel-edit-slide"
                    className="inline-flex items-center gap-1 text-xs text-[#FF4400] hover:text-[#FF6633] font-bold font-mono uppercase tracking-widest"
                  >
                    <Edit3 className="w-3 h-3" />
                    Modifica
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingIdx(-1)}
                      className="text-xs text-zinc-500 hover:text-white font-mono uppercase tracking-widest"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={saveEdit}
                      data-testid="carousel-save-slide"
                      className="text-xs text-[#FF4400] hover:text-[#FF6633] font-mono uppercase tracking-widest"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {editingIdx === activeSlide ? (
                <div className="space-y-3">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Headline
                    </label>
                    <input
                      type="text"
                      value={editDraft.headline}
                      onChange={(e) => setEditDraft({ ...editDraft, headline: e.target.value })}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-sm font-display font-bold focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                      data-testid="carousel-edit-headline"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      {carousel.slides[activeSlide]?.role === "body" ? "Paragrafo" : "Sottotitolo"}
                    </label>
                    <textarea
                      rows={5}
                      value={editDraft.body}
                      onChange={(e) => setEditDraft({ ...editDraft, body: e.target.value })}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-sm font-body focus:outline-none focus:ring-1 focus:ring-[#FF4400] resize-none"
                      data-testid="carousel-edit-body"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-display font-black text-lg text-white tracking-tight mb-2 leading-tight">
                    {carousel.slides[activeSlide]?.headline}
                  </div>
                  <div className="font-body text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                    {carousel.slides[activeSlide]?.body}
                  </div>
                </div>
              )}

              {/* Image controls — available for ALL slide types. Body slides
                  without an image use the brand gradient; adding a photo
                  switches the renderer to "image with text overlay" mode. */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Immagine di sfondo
                  </div>
                  {carousel.slides[activeSlide]?.image_base64 && (
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">
                      · {carousel.slides[activeSlide]?.image_prompt === "user-uploaded" ? "tua foto" : "AI"}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label
                    className="inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white rounded-sm transition-colors cursor-pointer"
                    data-testid="carousel-pick-gallery"
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span className="font-display font-bold text-xs">Foto</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Galleria</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={onPickImage(carousel.slides[activeSlide].slide_number)}
                    />
                  </label>
                  <label
                    className="inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white rounded-sm transition-colors cursor-pointer"
                    data-testid="carousel-pick-camera"
                  >
                    <Camera className="w-4 h-4" />
                    <span className="font-display font-bold text-xs">Foto</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Camera</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={onPickImage(carousel.slides[activeSlide].slide_number)}
                    />
                  </label>

                  {/* Video upload — gallery + camera. Available for every
                      slide. Instagram supports mixed image+video carousels
                      natively, so when a slide has a video the renderer
                      skips its PNG export and the download includes the
                      original .mp4 instead. */}
                  <label
                    className={`inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border rounded-sm transition-colors cursor-pointer ${
                      busyImageSlide === carousel.slides[activeSlide].slide_number
                        ? "border-zinc-900 text-zinc-600 cursor-not-allowed"
                        : "border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white"
                    }`}
                    data-testid="carousel-pick-video-gallery"
                  >
                    <VideoIcon className="w-4 h-4" />
                    <span className="font-display font-bold text-xs">Video</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Galleria</span>
                    <input
                      type="file"
                      accept="video/*"
                      disabled={busyImageSlide === carousel.slides[activeSlide].slide_number}
                      className="hidden"
                      onChange={onPickVideo(carousel.slides[activeSlide].slide_number)}
                    />
                  </label>
                  <label
                    className={`inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border rounded-sm transition-colors cursor-pointer ${
                      busyImageSlide === carousel.slides[activeSlide].slide_number
                        ? "border-zinc-900 text-zinc-600 cursor-not-allowed"
                        : "border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white"
                    }`}
                    data-testid="carousel-pick-video-camera"
                  >
                    <Camera className="w-4 h-4" />
                    <span className="font-display font-bold text-xs">Video</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Camera</span>
                    <input
                      type="file"
                      accept="video/*"
                      capture="environment"
                      disabled={busyImageSlide === carousel.slides[activeSlide].slide_number}
                      className="hidden"
                      onChange={onPickVideo(carousel.slides[activeSlide].slide_number)}
                    />
                  </label>

                  <button
                    onClick={() => onRegenerateImage(carousel.slides[activeSlide].slide_number)}
                    disabled={busyImageSlide === carousel.slides[activeSlide].slide_number || !!carousel.slides[activeSlide]?.video_id}
                    data-testid="carousel-regen-image"
                    className="inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white rounded-sm transition-colors disabled:opacity-40"
                  >
                    {busyImageSlide === carousel.slides[activeSlide].slide_number ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    <span className="font-display font-bold text-xs">Rigenera</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">AI da zero</span>
                  </button>
                  {carousel.slides[activeSlide]?.image_base64 && !carousel.slides[activeSlide]?.video_id && (
                    <button
                      onClick={() => setEditPromptOpen((v) => !v)}
                      data-testid="carousel-edit-with-ai"
                      className={`inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border rounded-sm transition-colors ${
                        editPromptOpen
                          ? "border-[#FF4400] bg-[#FF4400]/10 text-white"
                          : "border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white"
                      }`}
                    >
                      <Edit3 className="w-4 h-4" />
                      <span className="font-display font-bold text-xs">Modifica</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">con AI</span>
                    </button>
                  )}
                  {carousel.slides[activeSlide]?.video_id && (
                    <button
                      onClick={() => onRemoveSlideVideo(carousel.slides[activeSlide].slide_number)}
                      disabled={busyImageSlide === carousel.slides[activeSlide].slide_number}
                      data-testid="carousel-remove-video"
                      className="inline-flex flex-col items-center justify-center gap-1 px-3 py-3 border border-zinc-800 hover:border-red-500/60 text-zinc-400 hover:text-red-400 rounded-sm transition-colors disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="font-display font-bold text-xs">Rimuovi</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">video</span>
                    </button>
                  )}
                </div>

                {/* AI-edit prompt: only visible after the user clicks "Modifica con AI" */}
                {editPromptOpen && (
                  <div className="mt-3 space-y-2" data-testid="carousel-edit-prompt-panel">
                    <textarea
                      rows={2}
                      value={editPromptDraft}
                      onChange={(e) => setEditPromptDraft(e.target.value)}
                      placeholder="Es. cambia lo sfondo in un ufficio moderno, illumina il viso, aggiungi una palma..."
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-sm rounded-sm font-body focus:outline-none focus:ring-1 focus:ring-[#FF4400] resize-none"
                      data-testid="carousel-edit-prompt-input"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditPromptOpen(false); setEditPromptDraft(""); }}
                        className="px-3 py-2 text-xs text-zinc-400 hover:text-white font-mono uppercase tracking-widest"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={() => onAiEditWithPrompt(carousel.slides[activeSlide].slide_number)}
                        disabled={busyImageSlide === carousel.slides[activeSlide].slide_number}
                        data-testid="carousel-edit-prompt-submit"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold text-xs rounded-sm transition-colors"
                      >
                        {busyImageSlide === carousel.slides[activeSlide].slide_number ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        Applica modifica
                      </button>
                    </div>
                  </div>
                )}

                {!carousel.slides[activeSlide]?.image_base64 && carousel.slides[activeSlide]?.role === "body" && (
                  <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-zinc-600 leading-relaxed">
                    · Slide testuale su sfondo brand · aggiungi una foto per renderla "image+text"
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              {shareReady ? (
                <button
                  onClick={onShareNow}
                  data-testid="carousel-share-now"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-black text-lg rounded-sm transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                  Tocca per condividere
                </button>
              ) : (
                <button
                  onClick={onPrepareShare}
                  disabled={sharing || downloading}
                  data-testid="carousel-share"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold rounded-sm transition-colors"
                >
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  {sharing ? "Preparazione..." : "Condividi (Storie / Reels)"}
                </button>
              )}
              <button
                onClick={onDownload}
                disabled={downloading}
                data-testid="carousel-download"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-white font-display font-bold rounded-sm transition-colors disabled:opacity-40"
                title="Salva le slide complete (con scritte) nella galleria"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? "Download in corso..." : "Foto · Con scritte"}
              </button>
              <button
                onClick={onDownloadCleanPhotos}
                disabled={downloading}
                data-testid="carousel-download-clean"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-violet-500 hover:bg-violet-600 hover:border-violet-600 text-violet-300 hover:text-white font-display font-bold rounded-sm transition-colors disabled:opacity-40"
                title="Salva solo le foto di sfondo (senza nessuna scritta) nella galleria"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                Foto · Senza scritte
              </button>
              <button
                onClick={onCopyCaption}
                data-testid="carousel-copy-caption"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white font-body rounded-sm transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copia caption + hashtag
              </button>
              <button
                onClick={onDelete}
                data-testid="carousel-delete"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-800 hover:border-red-700 text-zinc-500 hover:text-red-500 font-body rounded-sm transition-colors text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Elimina carosello
              </button>
            </div>

            {/* Caption + hashtag block — always visible.
                Users (and IG copy-paste) need these prominent, not hidden. */}
            <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                  · Caption Instagram
                </div>
                <button
                  onClick={onCopyCaption}
                  data-testid="carousel-copy-caption-inline"
                  className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-[#FF4400] font-mono uppercase tracking-widest"
                >
                  <Copy className="w-3 h-3" />
                  Copia
                </button>
              </div>
              <div className="text-sm text-zinc-200 whitespace-pre-wrap font-body leading-relaxed">
                {stripTrailingHashtags(carousel.caption)}
              </div>
              {(carousel.hashtags || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Hashtag
                  </div>
                  <div className="text-sm text-[#FF4400] font-body break-words leading-relaxed">
                    {(carousel.hashtags || []).slice(0, 5).map((h) => `#${h}`).join(" ")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cross-create modal: ask "make also a reel from this idea?" */}
      <CrossCreatePrompt
        source={carousel}
        targetType="reel"
        mode="modal"
        open={crossOpen}
        onClose={() => setCrossOpen(false)}
      />
    </div>
  );
};

export default CarouselDetail;
